import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import TableFilter from "src/components/TableFilter";
import DateUtils from "src/services/utils/DateUtils";
import CommonService from "src/modules/data-protection/apis/CommonService";
import {
    HistoryLogSections,
    HistoryLogRoles,
} from "src/modules/data-protection/constants";

const FilteringComponent = ({
    t,
    onChange
}) => {
    const [tableFilters, setTableFilters] = useState({
        fromDate: null,
        toDate: null,
        user: [],
        roles: [],
        sections: []
    });

    const dateUtils = new DateUtils();

    const handleFetchUsersQuery = useQuery({
        queryKey: ["data-protection-users-list"],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchAnalystAndAdminAnalystList();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching users list."), {
                type: "error",
            });
        },
    });

    const tableFilterTabs = useMemo(() => {
        const sectionsList = (Object.keys(HistoryLogSections).map((section) => {
            return {
                id: section,
                title: t(HistoryLogSections[section])
            }
        }));

        const roles = (Object.keys(HistoryLogRoles).map((role) => {
            return {
                id: role,
                title: t(HistoryLogRoles[role])
            }
        }));

        return [
            {
                type: 'date',
                name: 'filter-date',
                title: 'Date',
                icon: <i className="ri-calendar-event-line" />,
                defaultValue: {
                    from: tableFilters['createdAt[from]'],
                    to: tableFilters['createdAt[to]']
                }
            },
            {
                type: 'check_list',
                name: 'roles',
                title: 'User Role',
                icon: <i className="ri-user-settings-line" />,
                defaultValue: tableFilters?.roles,
                data: roles,
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'check_list',
                name: 'user',
                title: 'User',
                icon: <i className="ri-user-line" />,
                isLoading: handleFetchUsersQuery.isFetching || handleFetchUsersQuery.isLoading,
                defaultValue: tableFilters?.user,
                data: handleFetchUsersQuery.data || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return `${item.first_name} ${item.last_name}`
                }
            },
            {
                type: 'check_list',
                name: 'sections',
                title: 'Activity',
                icon: <i className="ri-run-line" />,
                defaultValue: tableFilters?.sections,
                data: sectionsList,
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            }
        ]
    }, [
        tableFilters,
        handleFetchUsersQuery,
        t
    ]);

    const onTableFilterOkButtonClicked = (filters) => {
        if (filters) {
            const result = {}

            if ('filter-date' in filters) {
                result['fromDate'] = dateUtils.convertDateToDate(
                    new Date(filters['filter-date']?.from),
                    'YYYY-MM-DD 00:00:00'
                )

                result['toDate'] = dateUtils.convertDateToDate(
                    new Date(filters['filter-date']?.to),
                    'YYYY-MM-DD 23:59:59'
                )
            }

            result.user = filters.user?.length > 0 ? filters.user : undefined

            result.roles = filters.roles?.length > 0 ? filters.roles : undefined

            result.sections = filters.sections?.length > 0 ? filters.sections : undefined

            setTableFilters({
                ...tableFilters,
                ...result
            })
        } else {
            setTableFilters({
                fromDate: null,
                toDate: null,
                user: [],
                roles: [],
                sections: []
            })
        }
    }

    useEffect(() => {
        onChange && onChange(tableFilters)
    }, [
        tableFilters,
        onChange
    ]);

    return (
        <TableFilter
            className="me-2"
            defaultActiveTab='filter-date'
            tabs={tableFilterTabs}
            onOkButtonClicked={
                onTableFilterOkButtonClicked
            }
        />
    )
}

export default withNamespaces()(FilteringComponent);